import React, { useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Venue } from '../types/venue';
import { VenueCard } from './VenueCard';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useVenueStore } from '../store/useVenueStore';
import { useTranslation } from 'react-i18next';

interface VenueListProps {
  venues?: Venue[];
  selectedVenue?: string;
  onSelectVenue?: (venue: Venue) => void;
}

export const VenueList: React.FC<VenueListProps> = ({
  venues: propVenues,
  selectedVenue,
  onSelectVenue,
}) => {
  const { venues: storeVenues, filteredVenues } = useVenueStore();
  const { t } = useTranslation();
  const [displayHeight, setDisplayHeight] = useState(0);
  
  // Use filtered venues from store, or passed prop venues, or all venues from store
  const venues = filteredVenues || propVenues || storeVenues;
  
  // Split venues into recommended and non-recommended
  const recommendedVenues = venues.filter(venue => venue.recommended);
  const regularVenues = venues.filter(venue => !venue.recommended);

  // Effect to measure available vertical space
  useEffect(() => {
    const updateDisplayHeight = () => {
      const windowHeight = window.innerHeight;
      const headerHeight = 60; // Approximate header height
      const searchBarHeight = 60; // Approximate search bar height
      const availableHeight = windowHeight - headerHeight - searchBarHeight - 20; // 20px buffer
      setDisplayHeight(availableHeight);
    };

    updateDisplayHeight();
    window.addEventListener('resize', updateDisplayHeight);
    return () => window.removeEventListener('resize', updateDisplayHeight);
  }, []);

  const handleVenueClick = (venue: Venue) => {
    if (onSelectVenue) {
      onSelectVenue(venue);
    }
  };

  if (!venues || venues.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>{t('no_venues_found')}</p>
      </div>
    );
  }

  return (
    <div className="venue-list p-4">
      {/* Spotlight section for recommended venues - larger cards */}
      {recommendedVenues.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg text-gray-500 mb-3">{t('spotlight')}</h2>
          <div className="grid grid-cols-1 gap-4">
            {recommendedVenues.map(venue => (
              <VenueCard
                key={venue.id}
                venue={venue}
                isRecommended={true}
                isSelected={venue.id === selectedVenue}
                onClick={() => handleVenueClick(venue)}
                size="large"
              />
            ))}
          </div>
        </div>
      )}

      {/* Separator between sections */}
      {recommendedVenues.length > 0 && regularVenues.length > 0 && (
        <div className="border-b border-gray-200 my-8"></div>
      )}

      {/* Regular venues section - smaller cards */}
      <div>
        <h2 className="text-lg text-gray-500 mb-3">{t('all_venues')}</h2>
        <div className="grid grid-cols-1 gap-3">
          {regularVenues.map(venue => (
            <VenueCard
              key={venue.id}
              venue={venue}
              isSelected={venue.id === selectedVenue}
              onClick={() => handleVenueClick(venue)}
              size="small"
            />
          ))}
        </div>
      </div>
    </div>
  );
}; 